import { useQueryClient } from '@tanstack/react-query'
import { TFunction } from 'i18next'
import type { ColumnMapping } from '@/components/ExcelExportModal'
interface WeekDay {
    value: number
    label: string
    color: string
}

export const WEEK_DAYS: WeekDay[] = [
    { value: 1, label: 'T2', color: '#9e9e9e' },
    { value: 2, label: 'T3', color: '#66bb6a' },
    { value: 3, label: 'T4', color: '#FFA726' },
    { value: 4, label: 'T5', color: '#ab47bc' },
    { value: 5, label: 'T6', color: '#42a5f5' },
    { value: 6, label: 'T7', color: '#ef5350' },
    { value: 7, label: 'CN', color: '#78909c' },
]
export const getTextColor = (status: string) => {
    switch (status) {
        case 'pending':
            return 'text-yellow-500'
        case 'leader-approved':
        case 'approved':
            return 'text-green-500'
        case 'cancelled':
        case 'rejected':
            return 'text-red-500'
        default:
            return 'text-gray-500'
    }
}

export const getStatusColor = (status: string) => {
    switch (status) {
        case 'pending':
            return 'warning'
        case 'leader-approved':
            return 'success'
        case 'approved':
            return 'success'
        case 'cancelled':
            return 'error'
        case 'rejected':
            return 'error'
        default:
            return 'default'
    }
}
export const formatDate = (input: any): string => {
    const date = new Date(input)

    if (isNaN(date.getTime())) {
        throw new Error('Invalid date object')
    }

    const dayOfWeek = date.getDay()
    const weekDay = WEEK_DAYS.find(
        (day) => day.value === (dayOfWeek === 0 ? 7 : dayOfWeek)
    )
    const day = date.getDate().toString().padStart(2, '0')
    const month = (date.getMonth() + 1).toString().padStart(2, '0')
    const hours = date.getHours().toString().padStart(2, '0')
    const minutes = date.getMinutes().toString().padStart(2, '0')

    return `${weekDay?.label ?? ''} ${day}/${month} ${hours}:${minutes}`
}

export const BUSINESS_TYPE = (t: TFunction<'business'>) => [
    {
        label: t('meeting'),
        value: 'meeting',
    },
    {
        label: t('datacenter'),
        value: 'datacenter',
    },
    {
        label: t('others'),
        value: 'orthers',
    },
]

export const BUSINESS_STATUS = (t: TFunction<'business'>) => [
    {
        label: t('pending-business'),
        value: 'pending',
    },
    {
        label: t('leader-approved'),
        value: 'leader-approved',
    },
    {
        label: t('approved'),
        value: 'approved',
    },
    {
        label: t('cancelled'),
        value: 'cancelled',
    },
    {
        label: t('rejected'),
        value: 'rejected',
    },
]

export const BUSINESS_OPTION = (t: TFunction<'business'>) => [
    { label: t('morning'), value: '0' },
    { label: t('afternoon'), value: '1' },
    { label: t('all-day'), value: '2' },
]

export const MEETING_PURPOSE = (t: TFunction<'business'>) => [
    {
        label: t('meeting-purpose-presale'),
        value: 'presale_presentation',
    },
    {
        label: t('meeting-purpose-support'),
        value: 'support',
    },
    {
        label: t('meeting-purpose-relationship'),
        value: 'relationship',
    },
    {
        label: t('meeting-purpose-negotiation'),
        value: 'negotiation',
    },
]

const ALL_EMPLOYEE_COMPANION_PURPOSES = new Set([
    'support',
    'relationship',
    'negotiation',
])

export const allowsAllEmployeeCompanions = (meetingPurpose?: string) =>
    Boolean(
        meetingPurpose && ALL_EMPLOYEE_COMPANION_PURPOSES.has(meetingPurpose)
    )

export const REASON_UNPOTENTIAL = (t: TFunction<'business'>) => [
    {
        label: t('business-reason-budget'),
        value: 'budget',
    },
    {
        label: t('business-reason-target'),
        value: 'target',
    },
    {
        label: t('business-reason-target2'),
        value: 'target2',
    },
    {
        label: t('business-reason-provider'),
        value: 'provider',
    },
    {
        label: t('business-reason-others'),
        value: 'others',
    },
]

export const useRefetchBusinessQueries = () => {
    const queryClient = useQueryClient()

    const refetchAll = async () => {
        await Promise.all([
            queryClient.invalidateQueries({ queryKey: ['workday-list'] }),
            queryClient.invalidateQueries({ queryKey: ['business'] }),
            queryClient.invalidateQueries({ queryKey: ['business-index'] }),
        ])
    }

    return { refetchAll }
}

export const BUSINESS_EXCEL_COLUMNS: ColumnMapping[] = [
    { header: 'business_id', data_field: 'business_id' },
    { header: 'employee_name', data_field: 'employee_name' },
    { header: 'department_name', data_field: 'department_name' },
    { header: 'position', data_field: 'position_name' },
    {
        header: 'business_type',
        data_field: 'business_type',
    },
    { header: 'business_place', data_field: 'business_place' },
    { header: 'business_content', data_field: 'business_content' },
    {
        header: 'business_status',
        data_field: 'business_status',
    },
    {
        header: 'business_startdate',
        data_field: 'business_startdate',
        formatter_type: 'date',
        formatter_options: { date_format: 'DD/MM/YYYY' },
    },
    {
        header: 'business_enddate',
        data_field: 'business_enddate',
        formatter_type: 'date',
        formatter_options: { date_format: 'DD/MM/YYYY' },
    },
    {
        header: 'leader',
        data_field: 'business_leader_status',
    },
    {
        header: 'business_leader_feedback',
        data_field: 'business_leader_feedback',
    },
    {
        header: 'bom/bod',
        data_field: 'business_bod_status',
    },
    { header: 'business_bod_feedback', data_field: 'business_bod_feedback' },
    {
        header: 'business_created_at',
        data_field: 'business_created_at',
        formatter_type: 'date',
        formatter_options: { date_format: 'DD/MM/YYYY HH:mm' },
    },
]
